import { useState, useMemo } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, Loader2, AlertTriangle, Copy, Download, Clock,
  Coins, Hash, RefreshCw, ScrollText,
} from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  serpAnalysisApi,
  type AnalysisReport,
  type NgramItem,
  type SerpOrganicResult,
  type SerpFeature,
  type RankTrendItem,
  type RankChange,
} from "@/api/serp-analysis";
import HTagsTab from "@/components/HTagsTab";
import NavAnalysisTab from "@/components/NavAnalysisTab";
import AlibabaTab from "@/components/AlibabaTab";
import SeoDetailsTab from "@/components/SeoDetailsTab";
import QuestionsTab from "@/components/QuestionsTab";
import KwMapTab from "@/components/KwMapTab";
import AiLogModal from "@/components/ai-log-modal";

// ── Step name mapping ──

const STEP_TABS: { step: number; key: string; label: string }[] = [
  { step: 3, key: "competitor", label: "竞争者分析 Competitor" },
  { step: 4, key: "intent", label: "用户意图 Intent" },
  { step: 5, key: "opportunity", label: "機會識別 Opportunity" },
  { step: 6, key: "outline", label: "內容大綱 Outline" },
];

// ── Markdown renderer ──

function MarkdownContent({ content }: { content: string }) {
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
    </div>
  );
}

// ── Export helpers ──

function copyToClipboard(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success("已複製到剪貼簿"),
    () => toast.error("複製失敗"),
  );
}

function downloadMarkdown(text: string, filename: string) {
  const blob = new Blob([text], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Report tab content ──

function getReportBillingLabel(report: AnalysisReport): string {
  const r = report as any;
  if (r.llm_profile_key === 'openai_personal' || report.ai_model?.endsWith('@personal')) return 'Personal · OpenAI';
  if (r.llm_profile_key === 'cometapi_company' || report.ai_model?.endsWith('@company')) return 'Company · CometAPI';
  return r.llm_provider ? `Provider · ${r.llm_provider}` : 'Legacy · 未歸屬';
}

function isEstimatedUsdReport(report: AnalysisReport): boolean {
  const r = report as any;
  return Boolean(r.llm_profile_key || r.llm_provider || report.ai_model?.match(/@(personal|company)$/));
}

function formatEstimatedUsd(cost: number): string {
  return `US$${cost.toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`;
}

function ReportTab({ report, keyword, stepLabel }: { report: AnalysisReport | undefined; keyword: string; stepLabel: string }) {
  const [logOpen, setLogOpen] = useState(false);

  if (!report) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>此步驟尚無報告</p>
      </div>
    );
  }

  if (report.status === "running") {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-2 text-blue-500" />
        <p className="text-muted-foreground">分析進行中…</p>
      </div>
    );
  }

  if (report.status === "failed") {
    return (
      <div className="py-12 text-center">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2 text-red-500" />
        <p className="font-medium text-red-600">分析失敗</p>
        {report.error_message && (
          <p className="text-sm text-muted-foreground mt-2">{report.error_message}</p>
        )}
        <Button size="sm" variant="outline" className="mt-3" onClick={() => setLogOpen(true)}>
          <ScrollText className="h-3 w-3 mr-1" />
          查看 Log
        </Button>
        <AiLogModal open={logOpen} onOpenChange={setLogOpen} report={report} stepLabel={stepLabel} />
      </div>
    );
  }

  const markdown = report.report ?? "";
  const filename = `${keyword}-step${report.step}-${report.step_name}.md`;

  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="outline" onClick={() => setLogOpen(true)}>
          <ScrollText className="h-3 w-3 mr-1" />
          查看完整 Log
        </Button>
        <Button size="sm" variant="outline" onClick={() => copyToClipboard(markdown)}>
          <Copy className="h-3 w-3 mr-1" />
          複製 Copy
        </Button>
        <Button size="sm" variant="outline" onClick={() => downloadMarkdown(markdown, filename)}>
          <Download className="h-3 w-3 mr-1" />
          下載 MD
        </Button>
      </div>
      <AiLogModal open={logOpen} onOpenChange={setLogOpen} report={report} stepLabel={stepLabel} />

      {/* Markdown content */}
      <Card>
        <CardContent className="pt-6">
          {markdown ? (
            <MarkdownContent content={markdown} />
          ) : (
            <p className="text-muted-foreground text-center py-8">報告內容為空</p>
          )}
        </CardContent>
      </Card>

      {/* Step metadata */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground border-t pt-4">
        {report.ai_model && (
          <span>Model: {report.ai_model}</span>
        )}
        {report.duration_ms != null && (
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {(report.duration_ms / 1000).toFixed(1)}s
          </span>
        )}
        {(report.tokens_input != null || report.tokens_output != null) && (
          <span className="flex items-center gap-1">
            <Hash className="h-3 w-3" />
            {(report.tokens_input ?? 0).toLocaleString()} in / {(report.tokens_output ?? 0).toLocaleString()} out
          </span>
        )}
        {report.cost != null && (
          <span className="flex items-center gap-1">
            <Coins className="h-3 w-3" />
            {isEstimatedUsdReport(report)
              ? `${formatEstimatedUsd(report.cost)}（APP 估算）· ${getReportBillingLabel(report)}`
              : `${report.cost.toFixed(4)} Credits（舊制）`}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Ngram tab ──

interface FlatNgram {
  term: string;
  count: number;
}

function parseNgrams(items: NgramItem[], ngramKey: "ngrams_2" | "ngrams_3" | "ngrams_4"): FlatNgram[] {
  const agg: Record<string, number> = {};
  for (const item of items) {
    const ngrams = item[ngramKey];
    if (!Array.isArray(ngrams)) continue;
    for (const entry of ngrams) {
      const term = String((entry as Record<string, unknown>).term ?? (entry as Record<string, unknown>).ngram ?? "");
      const count = Number((entry as Record<string, unknown>).count ?? 1);
      if (term) {
        agg[term] = (agg[term] || 0) + count;
      }
    }
  }
  return Object.entries(agg)
    .map(([term, count]) => ({ term, count }))
    .sort((a, b) => b.count - a.count);
}

function NgramSection({ title, data }: { title: string; data: FlatNgram[] }) {
  const chartData = data.slice(0, 15);

  if (data.length === 0) {
    return (
      <div className="py-4 text-center text-muted-foreground text-sm">
        無 {title} 資料
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-semibold">{title}</h3>

      {/* Chart */}
      {chartData.length > 0 && (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ left: 120 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="term" width={110} tick={{ fontSize: 12 }} />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">#</TableHead>
            <TableHead>Term</TableHead>
            <TableHead className="text-right">Count</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.slice(0, 30).map((item, i) => (
            <TableRow key={item.term}>
              <TableCell className="text-muted-foreground">{i + 1}</TableCell>
              <TableCell className="font-mono text-sm">{item.term}</TableCell>
              <TableCell className="text-right">{item.count}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function NgramTab({ requestId }: { requestId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["analysis-ngrams", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getNgrams(requestId);
      return res.data;
    },
  });

  const ngrams2 = useMemo(() => parseNgrams(data?.items ?? [], "ngrams_2"), [data]);
  const ngrams3 = useMemo(() => parseNgrams(data?.items ?? [], "ngrams_3"), [data]);
  const ngrams4 = useMemo(() => parseNgrams(data?.items ?? [], "ngrams_4"), [data]);

  if (isLoading) {
    return (
      <div className="py-12 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center text-red-500">
        <AlertTriangle className="h-8 w-8 mx-auto mb-2" />
        <p>{(error as Error).message}</p>
      </div>
    );
  }

  if (!data?.items?.length) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        尚無 Ngram 資料
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <NgramSection title="2-gram" data={ngrams2} />
      <NgramSection title="3-gram" data={ngrams3} />
      <NgramSection title="4-gram" data={ngrams4} />
    </div>
  );
}

// ── SERP Results tab (raw DataForSEO data) ──

const TYPE_META: Record<string, { color: string; label: string; icon: string }> = {
  organic:               { color: "bg-blue-100 text-blue-700",    label: "Organic",           icon: "🔵" },
  paid:                  { color: "bg-yellow-100 text-yellow-700", label: "Paid Ad",           icon: "💰" },
  featured_snippet:      { color: "bg-purple-100 text-purple-700", label: "Featured Snippet",  icon: "⭐" },
  people_also_ask:       { color: "bg-green-100 text-green-700",   label: "People Also Ask",   icon: "❓" },
  knowledge_graph:       { color: "bg-indigo-100 text-indigo-700", label: "Knowledge Graph",   icon: "🧠" },
  ai_overview:           { color: "bg-violet-100 text-violet-700", label: "AI Overview",       icon: "🤖" },
  images:                { color: "bg-pink-100 text-pink-700",     label: "Image Pack",        icon: "🖼️" },
  video:                 { color: "bg-red-100 text-red-700",       label: "Video",             icon: "▶️" },
  local_pack:            { color: "bg-orange-100 text-orange-700", label: "Local Pack",        icon: "📍" },
  shopping:              { color: "bg-amber-100 text-amber-700",   label: "Shopping",          icon: "🛒" },
  top_stories:           { color: "bg-cyan-100 text-cyan-700",     label: "Top Stories",       icon: "📰" },
  discussions_and_forums:{ color: "bg-teal-100 text-teal-700",    label: "Discussions",       icon: "💬" },
  related_searches:      { color: "bg-gray-100 text-gray-600",    label: "Related Searches",  icon: "🔍" },
  find_results_on:       { color: "bg-slate-100 text-slate-600",  label: "Find Results On",   icon: "🔗" },
};

const FEATURE_ORDER = [
  "featured_snippet","ai_overview","paid","people_also_ask","knowledge_graph",
  "images","video","shopping","local_pack","top_stories","discussions_and_forums",
  "related_searches","people_also_search","find_results_on","perspectives",
];

function TypeBadge({ type }: { type: string }) {
  const meta = TYPE_META[type] ?? { color: "bg-gray-100 text-gray-600", label: type, icon: "📦" };
  return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${meta.color}`}>{meta.icon} {meta.label}</span>;
}

function FeaturePaa({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; expanded_element?: Array<{ description?: string; url?: string; domain?: string }> }>;
  return (
    <div className="space-y-2">
      {items.map((q, i) => (
        <div key={i} className="border rounded p-2">
          <p className="font-medium text-sm">{q.title ?? "—"}</p>
          {q.expanded_element?.map((e, j) => (
            <p key={j} className="text-xs text-muted-foreground mt-1">{e.description ?? ""} {e.domain ? `(${e.domain})` : ""}</p>
          ))}
        </div>
      ))}
    </div>
  );
}

function FeatureFeaturedSnippet({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-1 text-sm">
      {data.featured_title && <p className="font-semibold">{data.featured_title as string}</p>}
      {data.featured_description && <p className="text-muted-foreground">{data.featured_description as string}</p>}
      {data.url && <a href={data.url as string} className="text-blue-600 text-xs truncate block" target="_blank" rel="noopener noreferrer">{data.url as string}</a>}
    </div>
  );
}

function FeatureKnowledgeGraph({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-1 text-sm">
      {data.title && <p className="font-bold text-base">{data.title as string}</p>}
      {data.description && <p className="text-muted-foreground">{data.description as string}</p>}
      {data.website_url && <a href={data.website_url as string} className="text-blue-600 text-xs" target="_blank" rel="noopener noreferrer">{data.website_url as string}</a>}
    </div>
  );
}

function FeatureAiOverview({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; text?: string }>;
  return (
    <div className="space-y-1 text-sm">
      {items.map((it, i) => (
        <div key={i}>{it.title && <p className="font-medium">{it.title}</p>}{it.text && <p className="text-muted-foreground text-xs">{it.text}</p>}</div>
      ))}
    </div>
  );
}

function FeatureRelatedSearches({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string }>;
  return (
    <div className="flex flex-wrap gap-1">
      {items.map((it, i) => <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2 py-0.5 rounded">{it.title ?? "—"}</span>)}
    </div>
  );
}

function FeatureShopping({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; price?: number; currency?: string; domain?: string }>;
  return (
    <div className="space-y-1">
      {items.slice(0, 5).map((it, i) => (
        <div key={i} className="flex justify-between text-xs"><span>{it.title ?? "—"}</span><span className="font-medium">{it.currency}{it.price} <span className="text-muted-foreground">({it.domain})</span></span></div>
      ))}
    </div>
  );
}

function FeatureVideo({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; channel_name?: string; duration?: string }>;
  return (
    <div className="space-y-1">
      {items.slice(0, 5).map((it, i) => (
        <div key={i} className="text-xs"><span className="font-medium">{it.title ?? "—"}</span> <span className="text-muted-foreground">— {it.channel_name} {it.duration ? `(${it.duration})` : ""}</span></div>
      ))}
    </div>
  );
}

function FeatureLocalPack({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; address?: string; rating?: number }>;
  return (
    <div className="space-y-1">
      {items.slice(0, 5).map((it, i) => (
        <div key={i} className="text-xs"><span className="font-medium">{it.title ?? "—"}</span> <span className="text-muted-foreground">{it.address} {it.rating ? `★${it.rating}` : ""}</span></div>
      ))}
    </div>
  );
}

function FeatureTopStories({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; source?: string; date_published?: string }>;
  return (
    <div className="space-y-1">
      {items.slice(0, 5).map((it, i) => (
        <div key={i} className="text-xs"><span className="font-medium">{it.title ?? "—"}</span> <span className="text-muted-foreground">— {it.source}</span></div>
      ))}
    </div>
  );
}

function FeatureDiscussions({ data }: { data: Record<string, unknown> }) {
  const items = (data.items ?? []) as Array<{ title?: string; domain?: string }>;
  return (
    <div className="space-y-1">
      {items.slice(0, 5).map((it, i) => (
        <div key={i} className="text-xs"><span className="font-medium">{it.title ?? "—"}</span> <span className="text-muted-foreground">({it.domain})</span></div>
      ))}
    </div>
  );
}

function FeaturePaid({ data }: { data: Record<string, unknown> }) {
  return (
    <div className="space-y-1 text-xs">
      {data.title && <p className="font-medium">{data.title as string}</p>}
      {data.description && <p className="text-muted-foreground">{data.description as string}</p>}
      {data.url && <a href={data.url as string} className="text-blue-600" target="_blank" rel="noopener noreferrer">{data.domain as string || data.url as string}</a>}
    </div>
  );
}

function FeatureGeneric({ data }: { data: Record<string, unknown> }) {
  return <pre className="text-xs bg-muted p-2 rounded overflow-auto max-h-32">{JSON.stringify(data, null, 2)}</pre>;
}

function renderFeatureBlock(feature: SerpFeature) {
  const d = (feature.data_json ?? {}) as Record<string, unknown>;
  switch (feature.feature_type) {
    case "people_also_ask":       return <FeaturePaa data={d} />;
    case "featured_snippet":      return <FeatureFeaturedSnippet data={d} />;
    case "knowledge_graph":       return <FeatureKnowledgeGraph data={d} />;
    case "ai_overview":           return <FeatureAiOverview data={d} />;
    case "related_searches":      return <FeatureRelatedSearches data={d} />;
    case "shopping":              return <FeatureShopping data={d} />;
    case "video":                 return <FeatureVideo data={d} />;
    case "local_pack":            return <FeatureLocalPack data={d} />;
    case "top_stories":           return <FeatureTopStories data={d} />;
    case "discussions_and_forums":return <FeatureDiscussions data={d} />;
    case "paid":                  return <FeaturePaid data={d} />;
    default:                      return <FeatureGeneric data={d} />;
  }
}

function SerpResultsTab({ requestId }: { requestId: string }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["serp-results", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getSerpResults(requestId);
      return res.data;
    },
    enabled: !!requestId,
  });

  const queryClient = useQueryClient();
  const [isRefetching, setIsRefetching] = useState(false);

  if (isLoading) return <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;
  if (error) return <div className="py-8 text-center text-red-500">{(error as Error).message}</div>;
  if (!data) return null;

  const { organic = [], features = [] } = data;
  const isEmpty = organic.length === 0 && features.length === 0;

  if (isEmpty) {
    return (
      <div className="py-16 text-center space-y-4">
        <div className="text-4xl">🔍</div>
        <p className="font-medium">此關鍵字尚無 DataForSEO SERP 資料</p>
        <p className="text-sm text-muted-foreground">可能原因：資料尚未抓取或為舊版記錄</p>
        <Button
          onClick={async () => {
            setIsRefetching(true);
            try {
              const refreshRes = await serpAnalysisApi.refreshSerpResults(requestId);
              if ((refreshRes as { skipped?: boolean })?.skipped) {
                toast.info("今日已搜尋過此關鍵字，系統略過重複呼叫（節省點數）");
              } else {
                toast.success("SERP 資料已重新抓取，畫面更新中…");
              }
              await queryClient.invalidateQueries({ queryKey: ["serp-results", requestId] });
            } catch (e) {
              toast.error("重新抓取失敗：" + (e as Error).message);
            } finally {
              setIsRefetching(false);
            }
          }}
          disabled={isRefetching}
          className="gap-2"
        >
          {isRefetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          {isRefetching ? "抓取中…" : "🔄 重新抓取 DataForSEO 資料"}
        </Button>
        <p className="text-xs text-muted-foreground">每次約費用 $0.003~0.006</p>
      </div>
    );
  }

  const featuresByType: Record<string, SerpFeature[]> = {};
  for (const f of features) {
    if (!featuresByType[f.feature_type]) featuresByType[f.feature_type] = [];
    featuresByType[f.feature_type].push(f);
  }
  const orderedFeatureTypes = [
    ...FEATURE_ORDER.filter(t => featuresByType[t]),
    ...Object.keys(featuresByType).filter(t => !FEATURE_ORDER.includes(t)),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span>🔵 Organic: <strong className="text-foreground">{organic.length}</strong></span>
        <span>·</span>
        <span>Feature Blocks: <strong className="text-foreground">{orderedFeatureTypes.length} 類型</strong></span>
      </div>

      {orderedFeatureTypes.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold">SERP Feature Blocks</h3>
          {orderedFeatureTypes.map((type) => (
            <div key={type} className="border rounded p-3">
              <div className="flex items-center gap-2 mb-2"><TypeBadge type={type} /><span className="text-xs text-muted-foreground">{featuresByType[type].length} item(s)</span></div>
              {renderFeatureBlock(featuresByType[type][0])}
            </div>
          ))}
        </div>
      )}

      {organic.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2">Organic Results ({organic.length})</h3>
          <div className="space-y-2">
            {organic.map((item: SerpOrganicResult, idx: number) => (
              <div key={idx} className="border rounded p-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-xs text-muted-foreground w-6 flex-shrink-0">#{item.rank_absolute}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-medium truncate">{item.title ?? "—"}</p>
                      {item.item_type !== "organic" && <TypeBadge type={item.item_type} />}
                    </div>
                    <a href={item.url ?? "#"} className="text-blue-600 text-xs truncate block" target="_blank" rel="noopener noreferrer">{item.url ?? ""}</a>
                    {item.snippet && <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{item.snippet}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Rank Trend Tab ──

const TREND_COLORS = [
  "#2563eb", "#16a34a", "#dc2626", "#d97706", "#7c3aed",
  "#0891b2", "#db2777", "#65a30d", "#ea580c", "#6366f1",
];

function RankTrendTab({ keyword }: { keyword: string }) {
  const { data: trendsData, isLoading: trendsLoading } = useQuery({
    queryKey: ["rank-trends", keyword],
    queryFn: async () => {
      const res = await serpAnalysisApi.getRankTrends(keyword, 10);
      return res.data;
    },
    enabled: !!keyword,
  });

  const { data: changesData, isLoading: changesLoading } = useQuery({
    queryKey: ["rank-changes", keyword],
    queryFn: async () => {
      const res = await serpAnalysisApi.getRankChanges(keyword);
      return res.data;
    },
    enabled: !!keyword,
  });

  if (trendsLoading || changesLoading) {
    return <div className="py-12 text-center"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;
  }

  const trends = trendsData ?? [];
  const changes = changesData;

  if (trends.length === 0) {
    return (
      <div className="py-16 text-center space-y-2">
        <p className="text-muted-foreground">此關鍵字尚無排名快照</p>
        <p className="text-xs text-muted-foreground">每次搜尋 SERP 時會自動記錄排名</p>
      </div>
    );
  }

  const allDates = Array.from(
    new Set(trends.flatMap((t: RankTrendItem) => t.snapshots.map((s) => s.date)))
  ).sort();

  const chartData = allDates.map((date) => {
    const row: Record<string, string | number> = { date };
    trends.forEach((t: RankTrendItem) => {
      const snap = t.snapshots.find((s) => s.date === date);
      if (snap) row[t.domain] = snap.rank;
    });
    return row;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span>關鍵字：<strong className="text-foreground">{keyword}</strong></span>
        <span>快照日期：{allDates.length} 天</span>
        <span>追蹤域名：{trends.length} 個</span>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
            <YAxis reversed domain={[1, "auto"]} tick={{ fontSize: 10 }} label={{ value: "排名", angle: -90, position: "insideLeft", fontSize: 10 }} />
            <Tooltip formatter={(value: number, name: string) => [`#${value}`, name]} labelFormatter={(label: string) => `日期：${label}`} />
            {trends.map((t: RankTrendItem, i: number) => (
              <Line key={t.domain} type="monotone" dataKey={t.domain} stroke={TREND_COLORS[i % TREND_COLORS.length]} strokeWidth={2} dot={allDates.length <= 14} connectNulls={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>

      {changes && (
        <div>
          <h3 className="text-sm font-semibold mb-2">排名變化：{changes.previous_date || "?"} → {changes.current_date || "?"}</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/30 text-muted-foreground">
                  <th className="px-2 py-1.5 text-left">域名</th>
                  <th className="px-2 py-1.5 text-center w-16">上次</th>
                  <th className="px-2 py-1.5 text-center w-16">現在</th>
                  <th className="px-2 py-1.5 text-center w-20">變化</th>
                </tr>
              </thead>
              <tbody>
                {(changes.changes as RankChange[]).filter((c) => c.new_rank !== null || c.old_rank !== null).slice(0, 30).map((c, i) => (
                  <tr key={i} className="border-b hover:bg-accent/20">
                    <td className="px-2 py-1.5 truncate max-w-[200px]"><span className="font-medium">{c.domain}</span></td>
                    <td className="px-2 py-1.5 text-center text-muted-foreground">{c.old_rank ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center font-medium">{c.new_rank ?? "—"}</td>
                    <td className="px-2 py-1.5 text-center">
                      {c.status === "up" && <span className="text-green-600 font-bold">▲ {Math.abs(c.diff)}</span>}
                      {c.status === "down" && <span className="text-red-500 font-bold">▼ {Math.abs(c.diff)}</span>}
                      {c.status === "new" && <span className="text-purple-600 font-bold">✨ NEW</span>}
                      {c.status === "dropped" && <span className="text-orange-500 font-bold">❌ GONE</span>}
                      {c.status === "unchanged" && <span className="text-muted-foreground">=</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main page ──

const TOP_TABS = ["serp-results","serp-analysis","htags","nav-analysis","alibaba","seo-details","questions","kw-map","rank-trend"] as const;
const STEP_KEYS = ["competitor","intent","opportunity","outline","ngram"] as const;

export default function AnalysisReportDetail() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state synced with URL: ?tab=xxx&step=yyy
  const rawTab = searchParams.get('tab') ?? 'serp-analysis';
  const topTab = (TOP_TABS as readonly string[]).includes(rawTab) ? rawTab : 'serp-analysis';
  const setTopTab = (t: string) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('tab', t); return p; }, { replace: true });
  };
  const rawStep = searchParams.get('step') ?? 'competitor';
  const activeTab = (STEP_KEYS as readonly string[]).includes(rawStep) ? rawStep : 'competitor';
  const setActiveTab = (s: string) => {
    setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('step', s); return p; }, { replace: true });
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["analysis-results", requestId],
    queryFn: async () => {
      const res = await serpAnalysisApi.getResults(requestId!);
      return res.data;
    },
    enabled: !!requestId,
    refetchInterval: (query) => {
      // Auto-refresh if any step is still running
      const reports = query.state.data?.reports;
      if (reports?.some((r) => r.status === "running")) return 5_000;
      return false;
    },
  });

  // Build report map by step
  const reportsByStep = useMemo(() => {
    const map = new Map<number, AnalysisReport>();
    for (const r of data?.reports ?? []) {
      map.set(r.step, r);
    }
    return map;
  }, [data]);

  // Extract keyword from API response (joined from serp_requests)
  const keyword = data?.keyword ?? requestId ?? "Unknown";

  // Overall status
  const reports = data?.reports ?? [];
  const allCompleted = reports.length > 0 && reports.every((r) => r.status === "completed");
  const anyRunning = reports.some((r) => r.status === "running");
  const anyFailed = reports.some((r) => r.status === "failed");

  const totalDuration = reports.reduce((sum, r) => sum + (r.duration_ms ?? 0), 0);
  const totalTokens = reports.reduce((sum, r) => sum + (r.tokens_input ?? 0) + (r.tokens_output ?? 0), 0);

  if (!requestId) {
    return <div className="p-6">Invalid request ID</div>;
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/analysis-reports")}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          返回列表
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{keyword}</h1>
          <p className="text-sm text-muted-foreground">SERP 分析報告</p>
        </div>
        <div className="flex items-center gap-2">
          {allCompleted && <Badge className="bg-green-600">✅ 完成</Badge>}
          {anyRunning && <Badge variant="secondary" className="bg-blue-100 text-blue-800">🔄 分析中</Badge>}
          {anyFailed && <Badge variant="destructive">❌ 部分失敗</Badge>}
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">載入報告中…</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <p className="text-lg font-medium">無法載入報告</p>
            <p className="text-sm text-muted-foreground mt-2">{(error as Error).message}</p>
            <Button onClick={() => refetch()} variant="outline" className="mt-4">
              <RefreshCw className="h-4 w-4 mr-2" />
              重試
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Top-level category tabs */}
      {!isLoading && !error && (
        <Tabs value={topTab} onValueChange={setTopTab}>
          <TabsList>
            <TabsTrigger value="serp-results">🔍 SERP 結果</TabsTrigger>
            <TabsTrigger value="serp-analysis">SERP 分析</TabsTrigger>
            <TabsTrigger value="htags">H標籤彙整 H-Tags</TabsTrigger>
            <TabsTrigger value="nav-analysis">🧭 導覽列分析 Navigation</TabsTrigger>
            <TabsTrigger value="alibaba">🛒 Alibaba</TabsTrigger>
            <TabsTrigger value="seo-details">📝 SEO Details</TabsTrigger>
            <TabsTrigger value="questions">❓ Questions</TabsTrigger>
            <TabsTrigger value="kw-map">🏷️ Keyword Map</TabsTrigger>
            <TabsTrigger value="rank-trend">📈 排名趨勢</TabsTrigger>
          </TabsList>

          <TabsContent value="serp-analysis" className="mt-4">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid grid-cols-5 w-full">
                {STEP_TABS.map((tab) => (
                  <TabsTrigger key={tab.key} value={tab.key} className="text-xs sm:text-sm">
                    {tab.label}
                  </TabsTrigger>
                ))}
                <TabsTrigger value="ngram" className="text-xs sm:text-sm">
                  Ngram
                </TabsTrigger>
              </TabsList>

              {STEP_TABS.map((tab) => (
                <TabsContent key={tab.key} value={tab.key}>
                  <ReportTab report={reportsByStep.get(tab.step)} keyword={keyword} stepLabel={tab.label} />
                </TabsContent>
              ))}

              <TabsContent value="ngram">
                <NgramTab requestId={requestId} />
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="htags" className="mt-4">
            <HTagsTab requestId={requestId} />
          </TabsContent>

          <TabsContent value="nav-analysis" className="mt-4">
            <NavAnalysisTab requestId={requestId} />
          </TabsContent>

          <TabsContent value="alibaba" className="mt-4">
            <AlibabaTab keyword={keyword} />
          </TabsContent>

          <TabsContent value="seo-details" className="mt-4">
            <SeoDetailsTab requestId={requestId} keyword={keyword} />
          </TabsContent>

          <TabsContent value="questions" className="mt-4">
            <QuestionsTab requestId={requestId} />
          </TabsContent>

          <TabsContent value="kw-map" className="mt-4">
            <KwMapTab />
          </TabsContent>

          <TabsContent value="serp-results" className="mt-4">
            <SerpResultsTab requestId={requestId} />
          </TabsContent>

          <TabsContent value="rank-trend" className="mt-4">
            <RankTrendTab keyword={keyword} />
          </TabsContent>
        </Tabs>
      )}

      {/* Footer stats */}
      {!isLoading && !error && reports.length > 0 && (
        <div className="flex flex-wrap gap-6 text-sm text-muted-foreground border-t pt-4">
          <span className="flex items-center gap-1">
            <Clock className="h-4 w-4" />
            分析耗時: {(totalDuration / 1000).toFixed(1)}s
          </span>
          <span className="flex items-center gap-1">
            <Hash className="h-4 w-4" />
            Token: {totalTokens.toLocaleString()}
          </span>
          <span className="flex items-center gap-1">
            <Coins className="h-4 w-4" />
            LLM 估算費用: {formatEstimatedUsd(data?.total_cost ?? 0)}（非供應商正式帳單）
            {(() => { const labels = Array.from(new Set(((data as any)?.reports ?? []).filter((r: any) => r.status === 'completed').map((r: any) => getReportBillingLabel(r)))); return labels.length ? ` · 帳戶：${labels.join('、')}` : ''; })()}
          </span>
        </div>
      )}
    </div>
  );
}
